/**
 * IndexedDB voice queue storage layer
 * Offline-first queue for audio chunks and recording metadata.
 * Uses raw IndexedDB API wrapped in promises — no external dependencies.
 */

const DB_NAME = 'acn-voice-queue';
const DB_VERSION = 3;
const CHUNK_STORE = 'voice_queue';
const RECORDING_STORE = 'voice_recordings';
// v3 (TICKET-324): composer dictation drafts. Deliberately a SEPARATE store
// from RECORDING_STORE — queueWorker drains that one and would turn a
// composer dictation into a voice-note breadcrumb, and the queue tray would
// list it. These drafts are a crash/failure buffer for one-shot dictation
// only: written before the transcribe POST, deleted once text comes back.
const DICTATION_STORE = 'dictation_drafts';

let dbPromise = null;

/**
 * Open (or create) the IndexedDB database.
 * Reuses the same promise across calls so only one connection is opened.
 */
export function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      const tx = event.target.transaction;

      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        const chunkStore = db.createObjectStore(CHUNK_STORE, {
          keyPath: 'id',
          autoIncrement: true,
        });
        chunkStore.createIndex('recording_id', 'recording_id', { unique: false });
        chunkStore.createIndex('status', 'status', { unique: false });
      }

      let recordingStore;
      if (!db.objectStoreNames.contains(RECORDING_STORE)) {
        recordingStore = db.createObjectStore(RECORDING_STORE, { keyPath: 'recording_id' });
      } else if (tx) {
        recordingStore = tx.objectStore(RECORDING_STORE);
      }

      // v2: scope binding index for V-2's worker (pending-by-scope queries).
      if (recordingStore && !recordingStore.indexNames.contains('scope_type')) {
        recordingStore.createIndex('scope_type', 'scope_type', { unique: false });
      }

      // v3 (TICKET-324): composer dictation drafts.
      if (!db.objectStoreNames.contains(DICTATION_STORE)) {
        db.createObjectStore(DICTATION_STORE, { keyPath: 'draft_id' });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      // Same as TICKET-664 (src/lib/offline/db.js): WebKit force-closes the
      // connection on iOS backgrounding. Drop the cached promise so the next
      // op reopens instead of failing on the dead handle until reload.
      db.onclose = () => { dbPromise = null; };
      resolve(db);
    };
    request.onerror = () => reject(request.error);
  });
  // A failed open must not be cached forever: the next call retries fresh.
  const p = dbPromise.catch((err) => {
    if (dbPromise === p) dbPromise = null;
    throw err;
  });
  dbPromise = p;
  return dbPromise;
}

/**
 * Close the database connection and reset the cached promise.
 * Useful for tests that need to delete/recreate the database.
 */
export async function _closeDB() {
  if (dbPromise) {
    try {
      const db = await dbPromise;
      db.close();
    } catch {
      // ignore — db may not have opened successfully
    }
  }
  dbPromise = null;
}

/**
 * Reset the cached db promise (useful for tests).
 */
export function _resetDB() {
  dbPromise = null;
}

// ---------------------------------------------------------------------------
// Chunk operations
// ---------------------------------------------------------------------------

/**
 * Add an audio chunk to the queue.
 * Defaults: status = 'pending', retry_count = 0, created_at = now.
 * @returns {Promise<number>} The auto-generated chunk id.
 */
export async function addChunk(chunk) {
  const db = await openDB();
  const record = {
    ...chunk,
    status: chunk.status || 'pending',
    retry_count: chunk.retry_count ?? 0,
    created_at: chunk.created_at || Date.now(),
    error: chunk.error || null,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readwrite');
    const store = tx.objectStore(CHUNK_STORE);
    const req = store.add(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Get all chunks belonging to a recording.
 */
export async function getChunksByRecording(recordingId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readonly');
    const store = tx.objectStore(CHUNK_STORE);
    const index = store.index('recording_id');
    const req = index.getAll(recordingId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Get pending or failed chunks eligible for upload (retry_count < 10).
 * Ordered by created_at ascending.
 */
export async function getPendingChunks(limit = 10) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readonly');
    const store = tx.objectStore(CHUNK_STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      const results = req.result
        .filter(
          (c) =>
            (c.status === 'pending' || c.status === 'failed') &&
            c.retry_count < 10
        )
        .sort((a, b) => a.created_at - b.created_at)
        .slice(0, limit);
      resolve(results);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Update chunk status. Increments retry_count on failure.
 */
export async function updateChunkStatus(id, status, error = null) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readwrite');
    const store = tx.objectStore(CHUNK_STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (!record) {
        reject(new Error(`Chunk ${id} not found`));
        return;
      }
      record.status = status;
      record.error = error;
      if (status === 'failed') {
        record.retry_count = (record.retry_count || 0) + 1;
      }
      const putReq = store.put(record);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

/**
 * Delete a single chunk by id.
 */
export async function deleteChunk(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readwrite');
    const store = tx.objectStore(CHUNK_STORE);
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Delete uploaded chunks older than the given threshold (default 24 h).
 */
export async function deleteUploadedChunks(olderThanMs = 86400000) {
  const db = await openDB();
  const cutoff = Date.now() - olderThanMs;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readwrite');
    const store = tx.objectStore(CHUNK_STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      let deleted = 0;
      const toDelete = req.result.filter(
        (c) => c.status === 'uploaded' && c.created_at < cutoff
      );
      if (toDelete.length === 0) {
        resolve(deleted);
        return;
      }
      toDelete.forEach((c) => {
        store.delete(c.id);
        deleted++;
      });
      tx.oncomplete = () => resolve(deleted);
      tx.onerror = () => reject(tx.error);
    };
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Recording operations
// ---------------------------------------------------------------------------

/**
 * Create a recording metadata entry.
 */
export async function createRecording(recording) {
  const db = await openDB();
  const record = {
    ...recording,
    status: recording.status || 'recording',
    chunks_uploaded: recording.chunks_uploaded ?? 0,
    created_at: recording.created_at || Date.now(),
    scope_type: recording.scope_type ?? null,
    scope_id: recording.scope_id ?? null,
    optimistic_uuid: recording.optimistic_uuid ?? null,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(RECORDING_STORE, 'readwrite');
    const store = tx.objectStore(RECORDING_STORE);
    const req = store.put(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Get a recording by its recording_id.
 */
export async function getRecording(recordingId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(RECORDING_STORE, 'readonly');
    const store = tx.objectStore(RECORDING_STORE);
    const req = store.get(recordingId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Merge updates into an existing recording.
 */
export async function updateRecording(recordingId, updates) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(RECORDING_STORE, 'readwrite');
    const store = tx.objectStore(RECORDING_STORE);
    const getReq = store.get(recordingId);
    getReq.onsuccess = () => {
      const existing = getReq.result;
      if (!existing) {
        reject(new Error(`Recording ${recordingId} not found`));
        return;
      }
      const merged = { ...existing, ...updates };
      const putReq = store.put(merged);
      putReq.onsuccess = () => resolve(merged);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

/**
 * Get all recordings.
 */
export async function getAllRecordings() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(RECORDING_STORE, 'readonly');
    const store = tx.objectStore(RECORDING_STORE);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Recovery helpers — TICKET-VOICE-QUEUE-RECOVERY (CEO 2026-05-06 "Pase" stuck 166 min).
 *
 * Two failure modes the worker cannot self-recover from:
 *   - Server-side register_upload / process_upload returns a status the
 *     worker keeps retrying forever (auth expiry, RLS, transient 5xx).
 *   - The audio blob in IDB is good but the upload pipeline is wedged.
 * For both, the user needs (a) a way to force a fresh upload attempt
 * and (b) a way to download the audio out of IDB so the recording is
 * preserved as a local file even if the upload never succeeds.
 */

/**
 * Assemble all chunks for a recording into a single Blob, ordered by
 * chunk id (insertion order). Returns null if no chunks have an
 * `audio_blob` field. The MIME type defaults to audio/webm — adjust to
 * the recording's mime_type when the caller knows it.
 *
 * Duck-types the audio_blob check (truthy + has `size`) instead of using
 * `instanceof Blob`, because IndexedDB structured-clone roundtrips don't
 * always preserve the Blob prototype chain (notably under jsdom +
 * fake-indexeddb in tests, but also occasionally on production Safari
 * after IDB version upgrades). The Blob constructor accepts the cloned
 * objects directly via blob-parts iteration.
 */
function isBlobLike(b) {
  return !!b && typeof b === 'object' && (b instanceof Blob || typeof b.size === 'number');
}

export async function getRecordingBlob(recordingId, mimeType = 'audio/webm', chunksFetcher = getChunksByRecording) {
  const chunks = await chunksFetcher(recordingId);
  if (!chunks || chunks.length === 0) return null;
  const ordered = chunks
    .filter((c) => isBlobLike(c.audio_blob))
    .sort((a, b) => (a.id || 0) - (b.id || 0));
  if (ordered.length === 0) return null;
  return new Blob(
    ordered.map((c) => c.audio_blob),
    { type: mimeType },
  );
}

/**
 * Reset a recording's retry counters and status so the worker picks it
 * up immediately on the next tick. Idempotent. Used by the Force Retry
 * recovery action when the user knows the worker is stuck. Does NOT
 * delete the audio chunks — the next attempt re-uses them.
 */
export async function forceRetryRecording(recordingId) {
  return updateRecording(recordingId, {
    status: 'queued',
    retry_count: 0,
    next_retry_at: null,
    error_message: null,
    updated_at: Date.now(),
  });
}

// ---------------------------------------------------------------------------
// Dictation drafts (TICKET-324) — crash/failure buffer for the composer mic
// ---------------------------------------------------------------------------
//
// CEO 2026-07-22: a 5m11s email voice note was destroyed because useDictation
// held the only copy of the blob in a React ref and dropped it the moment the
// size guard tripped. The contract now: the blob is written here BEFORE the
// transcribe POST and removed ONLY after a transcript comes back. Any other
// outcome leaves a draft the user can retry or download.
//
// ponytail: no auto-prune. Every surviving draft is audio that FAILED to
// transcribe — i.e. the only copy of something the user said. Deleting it on
// an age timer is the exact data loss this ticket exists to stop; drafts go
// away on success or on an explicit user discard. If drafts ever pile up,
// that's a signal the transcribe path is broken, not a reason to sweep them.

/**
 * Write (or overwrite) a dictation draft. Idempotent on draft_id so the
 * mid-recording timeslice flush can keep re-stamping the same row.
 */
export async function saveDictationDraft(draft) {
  const db = await openDB();
  const record = {
    status: 'pending',
    attempts: 0,
    error_message: null,
    created_at: Date.now(),
    ...draft,
    updated_at: Date.now(),
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DICTATION_STORE, 'readwrite');
    const req = tx.objectStore(DICTATION_STORE).put(record);
    req.onsuccess = () => resolve(record);
    req.onerror = () => reject(req.error);
  });
}

/** Merge updates into an existing draft. No-op if it's already gone. */
export async function updateDictationDraft(draftId, updates) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DICTATION_STORE, 'readwrite');
    const store = tx.objectStore(DICTATION_STORE);
    const getReq = store.get(draftId);
    getReq.onsuccess = () => {
      const existing = getReq.result;
      if (!existing) { resolve(null); return; }
      const merged = { ...existing, ...updates, updated_at: Date.now() };
      const putReq = store.put(merged);
      putReq.onsuccess = () => resolve(merged);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

/** All drafts, newest first. */
export async function listDictationDrafts() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DICTATION_STORE, 'readonly');
    const req = tx.objectStore(DICTATION_STORE).getAll();
    req.onsuccess = () => resolve(
      (req.result || []).sort((a, b) => (b.created_at || 0) - (a.created_at || 0)),
    );
    req.onerror = () => reject(req.error);
  });
}

export async function getDictationDraft(draftId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DICTATION_STORE, 'readonly');
    const req = tx.objectStore(DICTATION_STORE).get(draftId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

/** Remove a draft. Called on transcribe success or explicit user discard. */
export async function deleteDictationDraft(draftId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DICTATION_STORE, 'readwrite');
    const req = tx.objectStore(DICTATION_STORE).delete(draftId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Queue statistics.
 */
export async function getQueueStats() {
  const db = await openDB();
  const [chunks, recordings] = await Promise.all([
    new Promise((resolve, reject) => {
      const tx = db.transaction(CHUNK_STORE, 'readonly');
      const req = tx.objectStore(CHUNK_STORE).getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }),
    new Promise((resolve, reject) => {
      const tx = db.transaction(RECORDING_STORE, 'readonly');
      const req = tx.objectStore(RECORDING_STORE).count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }),
  ]);

  return {
    totalChunks: chunks.length,
    pendingChunks: chunks.filter(
      (c) => c.status === 'pending' || c.status === 'failed'
    ).length,
    totalBytes: chunks.reduce((sum, c) => {
      if (c.audio_blob instanceof Blob) return sum + c.audio_blob.size;
      return sum;
    }, 0),
    recordingCount: recordings,
  };
}
